import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../services/api';
import DOMPurify from 'dompurify';
import toast from 'react-hot-toast'; // Importer la bibliothèque de notifications

/**
 * Transforme les URLs dans le texte en liens cliquables (ouvre dans un nouvel onglet).
 * @param {string} text
 * @returns {React.ReactNode}
 */
function linkifyText(text) {
  if (!text) return '';
  
  // Créer un élément temporaire pour stocker le résultat
  const result = [];
  
  // Expression régulière améliorée pour détecter les URLs
  const urlRegex = /(https?:\/\/|www\.)[a-z0-9-]+(\.[a-z0-9-]+)+([/?#].*)?/gi;
  
  // Trouver toutes les correspondances avec leur position
  let match;
  let lastIndex = 0;
  
  while ((match = urlRegex.exec(text)) !== null) {
    // Ajouter le texte avant l'URL
    if (match.index > lastIndex) {
      result.push(text.substring(lastIndex, match.index));
    }
    
    // Récupérer l'URL complète
    const url = match[0];
    
    // Ajouter l'URL sous forme de lien
    const fullUrl = url.startsWith('http') ? url : `https://${url}`;
    result.push(
      <a key={match.index} href={fullUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline break-all">
        {url}
      </a>
    );
    
    // Mettre à jour l'index pour la prochaine itération
    lastIndex = match.index + url.length;
  }
  
  // Ajouter le reste du texte après la dernière URL
  if (lastIndex < text.length) {
    result.push(text.substring(lastIndex));
  }
  
  return result;
}

/**
 * Parse le texte pour identifier les listes imbriquées (a), b), c) et tirets)
 * @param {string} text - Le texte à parser
 * @returns {Object} - Un objet contenant le texte principal et les sous-listes
 */
function parseNestedLists(text) {
  if (!text) return { mainText: text, items: [] };

  // Prétraitement pour éviter les faux positifs avec les parenthèses
  // Collecter toutes les expressions entre parenthèses comme (datant de moins de 2 ans)
  const parenthesesRegex = /\([^)]+\)/g;
  const parenthesesMatches = [];
  
  // Collecter toutes les expressions entre parenthèses
  let parenthesesMatch;
  while ((parenthesesMatch = parenthesesRegex.exec(text)) !== null) {
    parenthesesMatches.push({
      original: parenthesesMatch[0],
      start: parenthesesMatch.index,
      end: parenthesesMatch.index + parenthesesMatch[0].length
    });
  }
  
  // Vérifier si le texte contient des marqueurs de liste comme a), b), c)
  // qui ne font pas partie d'une expression entre parenthèses
  const letterMarkerRegex = /([a-z]\))/g;
  const validMarkers = [];
  
  let markerMatch;
  while ((markerMatch = letterMarkerRegex.exec(text)) !== null) {
    // Capturer les valeurs nécessaires pour éviter les références dangereuses dans la closure
    const matchIndex = markerMatch.index;
    const matchValue = markerMatch[0];
    
    // Vérifier si ce marqueur est dans une expression entre parenthèses
    const isInsideParentheses = parenthesesMatches.some(p => 
      matchIndex > p.start && matchIndex < p.end
    );
    
    if (!isInsideParentheses) {
      validMarkers.push({
        marker: matchValue,
        index: matchIndex
      });
    }
  }
  
  if (validMarkers.length === 0) {
    return { mainText: text, items: [] };
  }
  
  // Trier les marqueurs par position
  validMarkers.sort((a, b) => a.index - b.index);
  
  // Extraire le texte principal avant le premier marqueur valide
  const firstMarkerIndex = validMarkers[0].index;
  const mainText = text.substring(0, firstMarkerIndex).trim();
  
  // Extraire tous les items de la liste
  const items = [];
  
  // Traiter chaque section entre les marqueurs
  for (let i = 0; i < validMarkers.length; i++) {
    const currentMarker = validMarkers[i];
    const nextMarkerIndex = i < validMarkers.length - 1 ? validMarkers[i + 1].index : text.length;
    
    // Extraire le contenu entre ce marqueur et le suivant
    const startPos = currentMarker.index + currentMarker.marker.length;
    const sectionText = text.substring(startPos, nextMarkerIndex).trim();
    
    // Traiter les sous-items avec tirets
    const subItems = [];
    let content = sectionText;
    
    if (sectionText.includes(' - ')) {
      // Diviser par tirets tout en préservant le texte
      const dashParts = sectionText.split(/(?=\s-\s)/).filter(Boolean);
      
      if (dashParts.length > 0) {
        // Si le premier élément contient déjà un tiret au début
        if (dashParts[0].trim().startsWith('- ')) {
          // Tout est considéré comme des sous-items
          content = '';
          dashParts.forEach(part => {
            const trimmedPart = part.trim();
            if (trimmedPart.startsWith('- ')) {
              subItems.push(trimmedPart.substring(2).trim());
            } else {
              subItems.push(trimmedPart);
            }
          });
        } else {
          // Le premier élément est le contenu principal
          content = dashParts[0].trim();
          
          // Les éléments suivants sont des sous-items
          for (let i = 1; i < dashParts.length; i++) {
            const part = dashParts[i].trim();
            if (part.startsWith('- ')) {
              subItems.push(part.substring(2).trim());
            } else {
              subItems.push(part);
            }
          }
        }
      }
    }
    
    items.push({
      marker: currentMarker.marker,
      content,
      subItems
    });
  }
  
  return { mainText, items };
}


const ConsularAct = () => {
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [loading, setLoading] = useState(true);
  const [displayAct, setDisplayAct] = useState(null);
  const [error, setError] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);
  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;

    const fetchActDetails = async () => {
      setLoading(true);
      setError(null);

      try {
        let response;

        // Une coupure réseau ou un redémarrage du serveur ne doit pas faire échouer
        // immédiatement la page. Les erreurs HTTP fonctionnelles ne sont pas rejouées.
        for (let attempt = 0; attempt < 3; attempt += 1) {
          try {
            response = await api.getConsularActById(id);
            break;
          } catch (requestError) {
            const isTransientError = !requestError.response
              || [502, 503, 504].includes(requestError.response.status);

            if (!isTransientError || attempt === 2) {
              throw requestError;
            }

            await new Promise(resolve => setTimeout(resolve, 500 * (attempt + 1)));
          }
        }

        if (!cancelled) {
          setDisplayAct(response.data);
        }
      } catch (err) {
        if (cancelled) return;

        if (err.response) {
          switch (err.response.status) {
            case 403:
              setError('Accès refusé. Vérifiez vos permissions.');
              break;
            case 404:
              setError('Acte consulaire non trouvé.');
              break;
            case 500:
            case 502:
            case 503:
            case 504:
              setError('Erreur serveur. Veuillez réessayer plus tard.');
              break;
            default:
              setError("Une erreur est survenue lors du chargement de l'acte.");
          }
        } else if (err.request) {
          setError('Le service est momentanément indisponible. Veuillez réessayer.');
        } else {
          setError('Erreur de configuration de la requête.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    
    fetchActDetails();

    return () => {
      cancelled = true;
    };
  }, [id, reloadToken]);
  
  const handlePayment = () => {
    // Vérification des données avant navigation
    //console.log('Données de navigation :', {
    //  displayAct,
    //  category: displayAct?.category,
    //  service: displayAct?.category?.service
    //});

    // Vérification et construction sécurisée de l'URL
    if (!displayAct || !displayAct.category || !displayAct.category.service) {
      toast.error('Impossible de procéder au paiement. Données manquantes.');
      return;
    }

    // Construction de l'URL avec les bons identifiants
    const paymentUrl = `/payment?ser=${displayAct.category.service.id}&cat=${displayAct.category.id}&act=${displayAct.id}`;
    
    navigate(paymentUrl);
  };

  const handleDownloadForm = async () => {
    try {
      // Utiliser fetch pour télécharger le fichier car il s'agit d'une URL externe
      const response = await fetch(displayAct.form_url);
      const blob = await response.blob();

      // Extraire le nom de fichier de l'URL
      const filename = displayAct.form_url.substring(displayAct.form_url.lastIndexOf('/') + 1);
      
      // Créer un lien temporaire pour le téléchargement
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      
      // Nettoyer
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        //console.error('Erreur lors du téléchargement du formulaire:', error);
      } else {
        //console.error('Erreur lors du téléchargement du formulaire');
      }
      // Optionnel : afficher un message d'erreur à l'utilisateur
    }
  };

  const RequiredPartsSection = () => {
  const [openAccordion, setOpenAccordion] = useState(null);
  
  if (!displayAct.required_parts || displayAct.required_parts.length === 0) {
    return null;
  }

  const toggleAccordion = (index) => {
    setOpenAccordion(openAccordion === index ? null : index);
  };

  return (
    <div className="p-6">
      {displayAct.title.toLowerCase().includes('passeport') ? (
        <div className="text-gray-600 mb-6">
          <div dangerouslySetInnerHTML={{ 
            __html: DOMPurify.sanitize(
              displayAct.description
                .replace(/<h2><strong>IMPORTANT : AVANT TOUTE DEMANDE DE PASSEPORT BIOM[ÉE]TRIQUE<\/strong><\/h2>/gi, 
                  '<h2 class="text-red-600"><strong>IMPORTANT : AVANT TOUTE DEMANDE DE PASSEPORT BIOMÉTRIQUE</strong></h2>')
                .replace(/<p><strong>CES DEMARCHES SUIVANTES SONT [ÀA] EFFECTUER PAR L['']USAGER SE TROUVANT DANS L['']UN DES CAS CI-DESSOUS:<\/strong><\/p>/gi, 
                  '<p class="text-red-600"><strong>CES DEMARCHES SUIVANTES SONT À EFFECTUER PAR L\'USAGER SE TROUVANT DANS L\'UN DES CAS CI-DESSOUS:</strong></p>')
            ) 
          }} />
        </div>
      ) : (
        <div
          className="text-gray-600 mb-6"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(displayAct.description) }}
        />
      )}
      <h2 className="text-xl font-semibold text-gray-900 mb-6">Pièces requises</h2>

      <div className="space-y-4">
        {displayAct.required_parts.map((caseItem, caseIndex) => (
          <div 
            key={caseIndex} 
            className="border border-gray-200 rounded-lg overflow-hidden shadow-sm transition-all duration-300"
          >
            {/* Accordion Header */}
            <button 
              onClick={() => toggleAccordion(caseIndex)}
              className={`w-full flex justify-between items-center p-5 text-left focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary ${openAccordion === caseIndex ? 'bg-primary/5' : 'bg-white hover:bg-gray-50'}`}
            >
              <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                <span className="mr-3 flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </span>
                {caseItem.case}
              </h3>
              <span className="ml-4 flex-shrink-0 transition-transform duration-300 ease-in-out">
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  className={`h-6 w-6 text-gray-500 transform ${openAccordion === caseIndex ? 'rotate-180' : ''}`} 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </span>
            </button>
            
            {/* Accordion Content */}
            <div 
              className={`transition-all duration-500 ease-in-out overflow-hidden ${openAccordion === caseIndex ? 'opacity-100' : 'max-h-0 opacity-0'}`}
              style={{ maxHeight: openAccordion === caseIndex ? '2000px' : '0px' }}
            >
              <div className="p-5 bg-white border-t border-gray-100">
                <ul className="space-y-3">
                  {caseItem.parts.map((part, partIndex) => (
                    <li 
                      key={partIndex} 
                      className="flex items-start text-gray-700 group hover:bg-gray-50 p-2 rounded-md transition-colors duration-150"
                    >
                      <svg 
                        xmlns="http://www.w3.org/2000/svg" 
                        className="h-5 w-5 mr-3 mt-0.5 flex-shrink-0 text-green-500 group-hover:text-green-600 transition-colors duration-150" 
                        viewBox="0 0 20 20" 
                        fill="currentColor"
                      >
                        <path 
                          fillRule="evenodd" 
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" 
                          clipRule="evenodd" 
                        />
                      </svg>
                      <span className="flex-grow">
                        {(() => {
                          const { mainText, items } = parseNestedLists(part);
                          
                          if (items.length === 0) {
                            return linkifyText(part);
                          }
                          
                          return (
                            <div className="nested-list w-full">
                              {mainText && <div className="mb-2 font-medium">{linkifyText(mainText)}</div>}
                              <div className="space-y-3 mt-2">
                                {items.map((item, idx) => (
                                  <div key={idx} className="ml-1">
                                    <div className="flex items-start">
                                      <span className="font-medium text-primary mr-2 flex-shrink-0">{item.marker}</span>
                                      {item.content && <span className="text-gray-700">{linkifyText(item.content)}</span>}
                                    </div>
                                    
                                    {item.subItems.length > 0 && (
                                      <ul className="pl-6 mt-2 space-y-2">
                                        {item.subItems.map((subItem, subIdx) => (
                                          <li key={subIdx} className="flex items-start group hover:bg-gray-50 rounded-md p-1 -ml-1 transition-colors duration-150">
                                            <span className="text-green-600 mr-2 flex-shrink-0 mt-1">
                                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                                              </svg>
                                            </span>
                                            <span className="text-gray-700">{linkifyText(subItem)}</span>
                                          </li>
                                        ))}
                                      </ul>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })()}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

  // Fallback data for development/preview
  const fallbackAct = {
    id: id,
    title: "Passeport",
    description: "Le passeport est un document de voyage qui permet à son titulaire de certifier de son identité et de sa nationalité lors de ses déplacements à l'étranger.",
    required_parts: [
      "Formulaire de demande complété",
      "Photo d'identité récente",
      "Justificatif de domicile",
      "Ancien passeport (en cas de renouvellement)",
      "Timbre fiscal"
    ],
    shipping_fees: [
      { "Zone 1": 15 },
      { "Zone 2": 20 },
      { "Zone 3": 25 }
    ],
    processing_time: "15 jours ouvrables",
    form_url: "/forms/passport_form.pdf",
    category: { id: 1, name: "Documents d'identité" }
  };
  
  // Use actual data if available, otherwise use fallback
  const act = displayAct || fallbackAct;
  
  if (process.env.NODE_ENV !== 'production') {
    //console.error('🚨 DONNÉES COMPLÈTES REÇUES:', { 
    //  act, 
    //  requiredPartsType: act.required_parts ? typeof act.required_parts : 'undefined',
    //  requiredPartsStructure: act.required_parts ? JSON.stringify(act.required_parts) : 'N/A'
    //});
  }

  const renderRequestButton = () => {
    // Vérifier si l'acte est un passeport ou un visa
    const isPassport = act.title.toLowerCase().includes('passeport');
    const isVisa = act.title.toLowerCase().includes('visa');
    
    if (!isPassport && !isVisa) return null;

    if (isPassport) {
      return (
        <a 
          href="https://monpasseport.ci"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center px-6 py-4 min-h-[56px] border border-transparent rounded-md shadow-sm text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 w-full md:max-w-xs uppercase tracking-wide"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="-ml-1 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
          </svg>
          Faire la demande de passeport
        </a>
      );
    }
    
    if (isVisa) {
      // Ne pas afficher le bouton si les frais sont à 0 ou non définis
      if (typeof act.processing_fee !== 'number' || act.processing_fee <= 0) {
        return null;
      }
      
      return (
        <a 
          href="https://snedai.com/"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center px-5 py-3 border border-transparent rounded-md shadow-sm text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 w-full md:max-w-xs uppercase tracking-wide"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="-ml-1 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
          </svg>
          Payer les frais de visa
        </a>
      );
    }
    
    return null;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <svg className="animate-spin h-10 w-10 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-red-50 p-6 rounded-lg shadow-sm">
          <h2 className="text-xl font-semibold text-red-700 mb-2">Erreur</h2>
          <p className="text-red-600">{error}</p>
          <div className="mt-4 flex flex-wrap gap-3">
            <button
              onClick={() => setReloadToken(token => token + 1)}
              className="bg-primary hover:bg-primary/90 text-white font-bold py-2 px-4 rounded"
            >
              Réessayer
            </button>
            <button
              onClick={() => navigate(-1)}
              className="border border-primary text-primary hover:bg-primary/10 font-bold py-2 px-4 rounded"
            >
              Retour
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Breadcrumbs */}
      <nav className="mb-6">
        <ol className="flex items-center space-x-2 text-sm text-gray-500">
          <li>
            <button onClick={() => navigate('/')} className="hover:text-primary">
              Accueil
            </button>
          </li>
          <li>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </li>
          <li>
            <button onClick={() => navigate('/search')} className="hover:text-primary">
              Actes consulaires
            </button>
          </li>
          <li>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </li>
          <li className="font-medium text-primary">{act.title}</li>
        </ol>
      </nav>
      
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {/* Header */}
        <div className="bg-primary/10 p-4 sm:p-6 border-b border-primary/20">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div className="w-full">
              <h1 className="text-2xl font-bold text-gray-900">{act.title}</h1>
              {act.category && (
                <div className="mt-2">
                  <Link 
                    to={`/search?category=${act.category.id}`} 
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary/20 text-primary hover:bg-primary/30 transition-colors"
                  >
                    {act.category.name}
                  </Link>
                </div>
              )}
              
              {/* Informations supplémentaires */}
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-4 text-sm text-gray-700">
                <div className="bg-white/50 p-3 rounded-md">
                  <p className="font-bold">{act.title.toLowerCase().includes('passeport') ? 'Frais de passeport + RDV' : act.title.toLowerCase().includes('visa') ? 'Frais de visa' : act.title === 'Carte nationale d\'identité' ? 'Frais de timbre' : 'Droits de chancellerie'}</p>
                  <p className="text-lg font-semibold">
                    {typeof act.processing_fee !== 'number' || act.processing_fee <= 0 
                      ? 'Gratuit' 
                      : `${act.processing_fee} €`}
                  </p>
                  {!act.title.toLowerCase().includes('passeport') && typeof act.processing_fee === 'number' && act.processing_fee > 0 && (
                    <a 
                      href={act.title.toLowerCase().includes('visa') ? "https://snedai.com/" : act.title === 'Carte nationale d\'identité' ? "https://rnpp.ci" : "https://ci-embassyepay.org"} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="mt-2 inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-xs font-semibold text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary w-full uppercase tracking-wide"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                      </svg>
                      PAYER
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  )}
                </div>
                <div className="bg-white/50 p-3 rounded-md">
                  <p className="font-bold">Délai de traitement</p>
                  <p className="text-lg font-semibold">{act.processing_time}</p>
                </div>
                {act.shipping_fees && act.shipping_fees.length > 0 && act.title !== 'Carte consulaire' && act.title !== 'Carte nationale d\'identité' && !act.title.toLowerCase().includes('visa') && (
                  <div className="bg-white/50 p-3 rounded-md sm:col-span-2">
                    <p className="font-bold mb-2">Frais d&apos;expédition</p>
                    <div className="grid grid-cols-2 gap-2">
                      {act.shipping_fees.map((fee, index) => {
                        const zone = Object.keys(fee)[0];
                        const amount = fee[zone];
                        return amount && amount !== "" && amount > 0 ? (
                          <div key={index} className="flex justify-between items-center p-2 bg-white/70 rounded">
                            <span>{zone}</span>
                            <span className="font-bold">{amount} €</span>
                          </div>
                        ) : null;
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex flex-col !justify-center !items-center w-full gap-4 mt-4 md:mt-0">
              {/* Bouton de téléchargement du formulaire */}
              {act.form_url && 
               !act.title.toLowerCase().includes('passeport') && 
               !act.title.toLowerCase().includes('visa') && 
               act.title !== 'Carte nationale d\'identité' && (
                <button 
                  onClick={handleDownloadForm}
                  className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-xs font-semibold text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary w-full md:max-w-xs uppercase tracking-wide"
                >
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    className="h-5 w-5 mr-2" 
                    viewBox="0 0 20 20" 
                    fill="currentColor"
                  >
                    <path 
                      fillRule="evenodd" 
                      d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-9.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" 
                      clipRule="evenodd" 
                    />
                  </svg>
                  Télécharger le formulaire
                </button>
              )}
              
              {/* Bouton de paiement des frais d'expédition */}
              {act.shipping_fees && act.shipping_fees.length > 0 && act.title !== 'Carte consulaire' && act.title !== 'Carte nationale d\'identité' && !act.title.toLowerCase().includes('visa') && (
                <div className="flex flex-col items-center w-full">
                  <button 
                    onClick={handlePayment}
                    className="inline-flex items-center justify-center px-6 py-3 border border-transparent rounded-md shadow-sm text-sm font-semibold text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary w-full md:max-w-xs uppercase tracking-wide"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 min-w-[20px] min-h-[20px] mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    {act.title.toLowerCase().includes('passeport')
  ? "Payer les frais d'expédition du passeport"
  : "Payer les frais d'expédition des actes établis"
}
                  </button>
                  <small className={`mt-2 block text-lg font-bold text-center ${
                    act.title.toLowerCase().includes('passeport') 
                      ? 'text-red-600' 
                      : 'text-gray-600'
                  }`}>
                    {act.title.toLowerCase().includes('passeport') 
                      ? 'Le paiement des frais d\'expédition est obligatoire.' 
                      : 'Le paiement des frais d\'expédition est facultatif.'}
                  </small>
                </div>
              )}
              


              {/* Bouton pour la prise de RDV - sur tous les actes sauf visas et passeports */}
              {!act.title.toLowerCase().includes('visa') && 
               !act.title.toLowerCase().includes('passeport') && (
                <div className="flex flex-col sm:flex-row gap-4 w-full justify-center">
                  <a 
                    href="https://eqrco.de/a/AAfNd8" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center px-6 py-3 border border-gray-300 rounded-md shadow-sm text-sm font-semibold text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary uppercase tracking-wide"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    Prendre RDV
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-2 text-gray-500/70" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                </div>
              )}

              {renderRequestButton()}
            </div>
          </div>
        </div>
        
        <RequiredPartsSection />
        
        <div className="flex flex-col sm:flex-row gap-4 mt-6 p-6">
          <button 
            onClick={() => navigate(-1)}
            className="inline-flex items-center justify-center px-6 py-3 border border-gray-300 rounded-md shadow-sm text-sm font-semibold text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary uppercase tracking-wide"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Retour
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConsularAct;
